import React, { useEffect, useMemo, useRef } from 'react';
import '@/pages/opinion/assets/scss/pages/feeds/GameStore.scss';
import { connect, useDispatch, useIntl } from 'umi';
import FilterBoxCommon from '@/pages/opinion/components/FilterBoxCommon';
import CardCommon from '@/pages/opinion/components/CardCommon';
import KeywordBox from '@/pages/opinion/pages/feeds/components/KeywordBox';
import CardLoading from '@/pages/opinion/components/CardLoading';
import {
  FilterFixed,
  useFilterDataHook,
} from '@/pages/opinion/components/FilterCommon';
import GameStoreUserScore from './GameStoreUserScore';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import useSentimentSelect from '../../hooks/useSentimentSelect';
import { useInitBuriedPointInfo } from '@/pages/opinion/operationLog/buriedPoint';
import { getSearchText } from '../../constants';
import { getRealParams } from '@/pages/opinion/utils/params';
import { useGameStoreColumns } from '@/pages/opinion/components/TableCommonHook/hooks/useGameStoreColumns';
import TableCommonHook from '@/pages/opinion/components/TableCommonHook';
import useFeedsData from '../../hooks/useFeedsData';
import { useRequestLatestEditLog } from '../../hooks/useFeedsRequest';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { useWindowSize } from '@op/hooks/useResize';
import { useFeedsGpt } from '../../hooks/useFeedsGpt';
import {
  beforeCacheDealwithData,
  getSortOptions,
} from '../../constants/filter';
import { buildSearchParams } from '../../utils/searchMode';
const defaultTableList: any = [];
function GameStore(props: any) {
  const {
    userInfo,
    gameInfo,
    wholeGameInfo,
    feedsStats,
    feedsKeyword,
    feedsComments,
    feedsCommentsLoading = true,
    feedsStatsLoading = true,
    feedsKeywordLoading = true,
    feedsSearchComments,
    feedsSearchCommentsLoading = true,
    scrollBody,
    tableSticky = true,
    showAigcBtn = false,
    buriedPointInfo,
    specialParams,
    hasSteam,
  } = props;

  const { formatMessage } = useIntl();
  useInitBuriedPointInfo({ buriedPoint: buriedPointInfo });
  const dispatch = useDispatch();
  const { Sentiment_Rating } = useGetSingePageSourceRule({
    keys: 'Sentiment_Rating',
  });
  const { download } = useDownloadParams();
  const {
    pageInfo,
    sortBy,
    matchModel,
    search,
    selectKeyword,
    defaultKeyword,
    selectRenderKeyword,
    isExcludeInvalid,
    hasSearch,
    isExactOrExtendSearch,
    filterBoxValue,
    changePageInfo,
    setSortBy,
    setIsExcludeInvalid,
    resetPartFeedsData,
    filterBoxChange,
    filterBoxSearch,
    filterBoxClear,
    keywordChange,
    showSentiment,
    languages,
    setLanguages,
    channelList,
    setChannelList,
  } = useFeedsData({
    storageKey: `feedsGameStore_${gameInfo.edition_unified_id}`,
  });
  const { comments, loading, tableList } = useMemo(() => {
    const comments = isExactOrExtendSearch ? feedsSearchComments : feedsComments;
    const loading = isExactOrExtendSearch
      ? feedsSearchCommentsLoading
      : feedsCommentsLoading;
    return {
      comments,
      loading,
      tableList: comments?.list || defaultTableList,
    };
  }, [
    isExactOrExtendSearch,
    feedsSearchComments,
    feedsComments,
    feedsSearchCommentsLoading,
    feedsCommentsLoading,
  ]);

  const type = isExactOrExtendSearch
    ? 'opinionFeeds/getFeedsSocialMediaSearchComments'
    : 'opinionFeeds/getFeedsSocialMediaComments';
  // const { hasSentimentSentences, setHasSentimentSentences } =
  //   useSentimentSentences({});
  const {
    loading: initLoading,
    filter,
    changeFilter,
    filterOptions,
    isFirst,
    filterCommonParams,
  } = useFilterDataHook({
    gameInfo,
    hasHero: true,
    pageName: 'Feeds_Game_Store',
  });

  const commonParams = useMemo(() => {
    // 模糊搜索使用 search_content 字段，精准/拓展搜索使用 search_data 字段
    const searchParams = buildSearchParams(matchModel, search, hasSearch);
    return {
      purpose: [],
      sort_item: sortBy.by,
      sort_model: sortBy.order,
      ...pageInfo,
      ...specialParams,
      // 模糊搜索不隐藏 keyword，精准/拓展搜索隐藏 keyword
      keyword: isExactOrExtendSearch ? [] : selectKeyword,
      ...searchParams,
    };
    // isExactOrExtendSearch 已包含 matchModel 变化逻辑，不需要单独把 matchModel 放入依赖
  }, [pageInfo, sortBy, search, selectKeyword, isExactOrExtendSearch, hasSearch, specialParams]);

  const fetchComments = () => {
    if (!userInfo || !gameInfo || !filter) return;
    const realParams = getRealParams({
      filter,
      commonParams,
      isExcludeInvalid,
    });
    dispatch({
      type,
      payload: {
        ...gameInfo,
        ...realParams,
        user_id: userInfo.userid,
        ...pageInfo,
        need_topic_translation: true,
        // `/feeds/comments` 接口不接受 match_model，仅 `/feeds/search` 需要
        ...(isExactOrExtendSearch ? { match_model: matchModel } : {}),
      },
    });
  };

  useEffect(() => {
    fetchComments();
  }, [userInfo, commonParams, filter, isExcludeInvalid]);

  // 仅处理 commonParams 不变（exact↔extend）的 matchModel 切换，避免重复请求
  const prevIsExactOrExtendSearchRef = useRef(isExactOrExtendSearch);
  useEffect(() => {
    const prev = prevIsExactOrExtendSearchRef.current;
    prevIsExactOrExtendSearchRef.current = isExactOrExtendSearch;

    // 无搜索内容或 isExactOrExtendSearch 已变（由主 effect 通过 commonParams 处理），跳过
    if (!hasSearch || prev !== isExactOrExtendSearch) return;

    fetchComments();
  }, [matchModel]);

  useEffect(() => {
    if (!userInfo || !gameInfo || !filter) return;

    dispatch({
      type: 'opinionFeeds/getFeedsStats',
      payload: {
        ...filter,
        ...gameInfo,
        ...specialParams,
        user_id: userInfo.userid,
        review_type: 'all',
      },
    });

    dispatch({
      type: 'opinionFeeds/getFeedsKeyword',
      payload: {
        ...filter,
        ...gameInfo,
        ...specialParams,
        user_id: userInfo.userid,
        keyword_sample_size: '500',
        review_type: 'all',
      },
    });
  }, [userInfo, filter]);

  const { sentimentSelect, setSentimentSelect } = useSentimentSelect({
    filter,
    changeFilter,
  });

  const { GptBtnElement, GptElement } = useFeedsGpt({
    filter,
    commonParams,
    gameInfo,
    specialParams,
    // `/feeds/comments` 接口不接受 match_model，仅 `/feeds/search` 需要
    ...(isExactOrExtendSearch ? { match_model: matchModel } : {}),
    feedsStats,
    wholeGameInfo,
    gptName: 'Game Store',
  });

  const { logCommentMap, loading: logLoading } = useRequestLatestEditLog({
    data: tableList as any,
  });

  const { columns, selectSortKeys, sortColumns } = useGameStoreColumns({
    gameInfo,
    languages,
    renderKey: hasSearch ? search : selectRenderKeyword,
    sort: sortBy,
    setSort: setSortBy,
    isExcludeInvalid,
    setIsExcludeInvalid,
    showSentiment,
    total: hasSearch ? comments?.total : undefined,
    logCommentMap,
    logLoading,
    setLanguages,
    channelList,
    wholeGameInfo
  });

  const leftOptions = useMemo(() => {
    const sortKeys = [...selectSortKeys];

    if (hasSearch) {
      sortKeys.unshift('relevance');
    } else {
      if (gameInfo.edition_unified_id === 'ufc454d9b1af70b40588e2a6fa4da4a8b') {
        sortKeys.unshift('order_number');
      }

      if (
        (filter?.channel?.includes('steam') || filter?.channel?.length === 0) &&
        gameInfo.entity_type === 'pc'
      ) {
        sortKeys.unshift('record_duration');
      }
    }
    const orderOptions = getSortOptions(sortKeys);

    const options: Array<any> = [
      {
        key: 'gpt',
        show: showAigcBtn && hasSearch,
        element: GptBtnElement,
      },
      {
        key: 'sortBy',
        valueKey: 'sortBy-1',
        defaultValue: sortBy.by,
        defaultParams: {
          options: orderOptions,
        },
        show: !hasSearch,
      },
      // {
      //   key: 'singleCheckbox',
      //   valueKey: 'showSentiment',
      //   defaultValue: showSentiment,
      //   defaultParams: {
      //     text: 'Sub Sentiment',
      //     tipsMap: commonSentimentSentencesTips,
      //   },
      //   show: !Sentiment_Rating,
      // },
      {
        key: 'sortBy',
        valueKey: 'sortBy-2',
        defaultValue: sortBy.by,
        defaultParams: {
          options: orderOptions,
        },
        show: hasSearch,
      },
    ];

    return options;
  }, [
    gameInfo,
    filter,
    showAigcBtn,
    search,
    comments?.total,
    sortBy,
    showSentiment,
    selectSortKeys,
  ]);

  const { windowSize } = useWindowSize();

  return (
    <div className="feeds-game_store">
      <FilterFixed
        filter={filterOptions}
        confirm={(value: any) => {
          if (!isFirst) {
            resetPartFeedsData();
            setSentimentSelect(undefined);
          }
          if (gameInfo.entity_type === 'pc') {
            value.steam_filter_type = 'steam';
          }
          changeFilter(value);
        }}
        defaultConfig={{ channel: { channelKey: 'comments' } }}
        otherRequestType={['sentiment_sentences_config']}
        scrollBody={scrollBody}
        defaultValue={
          gameInfo.entity_type !== 'mobile' && hasSteam
            ? { channel_comments: ['steam'] }
            : {}
        }
        {...filterCommonParams}
        beforeCacheDealwithData={beforeCacheDealwithData}
        backCondOriginData={(data) => {
          const { channel } = data;
          setChannelList(channel?.comments || []);
        }}
      ></FilterFixed>
      <CardLoading loading={initLoading}>
        {/* 模糊搜索不隐藏 keyword，精准/拓展搜索隐藏 keyword */}
        {!isExactOrExtendSearch && (
          <>
            <GameStoreUserScore
              gameInfo={gameInfo}
              isFeeds
              filter={filter}
            ></GameStoreUserScore>

            <CardCommon
              loading={feedsKeywordLoading || feedsStatsLoading}
              loadingHeight={200}
            >
              <KeywordBox
                keywords={feedsKeyword}
                data={feedsStats}
                defaultSelectedKeyword={defaultKeyword}
                tableTotal={comments?.total}
                commentLoading={feedsCommentsLoading}
                keywordChange={keywordChange}
                interactable
                keyMetricsKey="game_store"
                setSentimentSelect={(value: any) => {
                  changePageInfo();
                  setSentimentSelect(value);
                }}
                sentimentSelect={sentimentSelect}
                GptBtnElement={GptBtnElement}
              />
            </CardCommon>
          </>
        )}

        {GptElement}

        <FilterBoxCommon
          left={leftOptions}
          right={[
            [
              {
                key: 'match_model',
                defaultValue: matchModel,
              },
              {
                key: 'search',
                defaultValue: search,
                defaultParams: {
                  className: 'dbo-search-input',
                  placeholder: formatMessage({
                    id: getSearchText({
                      isSearch: hasSearch,
                      width: windowSize.width,
                    }),
                  }),
                  onSearch: filterBoxSearch,
                  onClear: filterBoxClear,
                },
              },
            ],
            {
              key: 'download',
              event: {
                onClick: () => {
                  if (!comments?.total) return;

                  const fileName = `Game Store Feeds`;

                  const downloadFilter = {
                    ...filter,
                    ...gameInfo,
                    ...commonParams,
                    ...specialParams,
                    page: 1,
                    page_size: 1000,
                    // `/feeds/comments` 接口不接受 match_model，仅 `/feeds/search` 需要
                    ...(isExactOrExtendSearch ? { match_model: matchModel } : {}),
                  };

                  download({
                    key: 'FeedsGameStoreDownload',
                    fileName,
                    filter: downloadFilter,
                    auxiliaryParams: {
                      hideSentimentRating: Sentiment_Rating,
                      entity_type: gameInfo.entity_type,
                      isExactOrExtendSearch,
                    },
                  });
                },
              },
            },
          ]}
          onChange={filterBoxChange}
          minModal={!Sentiment_Rating}
          className={hasSearch ? 'dbo-search-info' : ''}
        ></FilterBoxCommon>

        <CardCommon
          loading={loading}
          loadingHeight={300}
        // empty={isEmpty}
        // emptyHeight={300}
        >
          <TableCommonHook
            tableSticky={tableSticky}
            records={comments?.list || []}
            total={comments?.total || 0}
            pageInfo={pageInfo}
            setPageInfo={changePageInfo}
            sortColumns={sortColumns}
            columns={columns}
            sortInfo={[sortBy, setSortBy]}
          />
        </CardCommon>
      </CardLoading>
    </div>
  );
}

export default connect((state: any) => {
  const { loading, home, opinionCommon, opinionFeedsFilter, opinionFeeds } =
    state;

  return {
    userInfoLoading: loading.effects['home/getUserInfo'],
    userInfo: home.userInfo,
    gameInfo: opinionCommon.gameInfo,
    wholeGameInfo: opinionCommon.wholeGameInfo,
    filter: opinionFeedsFilter,

    feedsStats: opinionFeeds.stats,
    feedsStatsLoading: loading.effects['opinionFeeds/getFeedsStats'],

    feedsKeyword: opinionFeeds.keyword,
    feedsKeywordLoading: loading.effects['opinionFeeds/getFeedsKeyword'],

    feedsComments: opinionFeeds.feedsSocialMediaComments,
    feedsCommentsLoading:
      loading.effects['opinionFeeds/getFeedsSocialMediaComments'],

    feedsSearchComments: opinionFeeds.feedsSocialMediaSearchComments,
    feedsSearchCommentsLoading:
      loading.effects['opinionFeeds/getFeedsSocialMediaSearchComments'],
  };
})(GameStore);
